class ArsenioHeader extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-arsenio-menu-toggle]');
    this.drawer = this.querySelector('[data-arsenio-menu-drawer]');

    if (!this.button || !this.drawer) return;

    this.button.addEventListener('click', () => this.toggleMenu());
    this.drawer.addEventListener('click', (event) => {
      if (event.target.closest('a')) this.closeMenu();
    });
    this.keydownHandler = (event) => {
      if (event.key === 'Escape') this.closeMenu();
    };
    document.addEventListener('keydown', this.keydownHandler);
    this.setMenuState(false);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.keydownHandler);
    document.documentElement.classList.remove('arsenio-menu-open');
    document.body.classList.remove('arsenio-menu-open');
  }

  toggleMenu() {
    this.setMenuState(this.dataset.menuOpen !== 'true');
  }

  closeMenu() {
    this.setMenuState(false);
  }

  setMenuState(isOpen) {
    this.dataset.menuOpen = String(isOpen);
    this.button.setAttribute('aria-expanded', String(isOpen));
    this.button.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    this.button.querySelector('[data-arsenio-menu-label="open"]').hidden = isOpen;
    this.button.querySelector('[data-arsenio-menu-label="close"]').hidden = !isOpen;
    document.documentElement.classList.toggle('arsenio-menu-open', isOpen);
    document.body.classList.toggle('arsenio-menu-open', isOpen);
  }
}

if (!customElements.get('arsenio-header')) {
  customElements.define('arsenio-header', ArsenioHeader);
}

function initArsenioMagnifiers(root = document) {
  root.querySelectorAll('.arsenio-product-card__media').forEach((media) => {
    if (media.dataset.magnifierReady === 'true') return;

    const sourceImage = media.querySelector('.arsenio-product-card__image');
    if (!sourceImage) return;

    media.dataset.magnifierReady = 'true';

    const lens = document.createElement('span');
    const canvas = document.createElement('span');
    const zoomImage = sourceImage.cloneNode(true);

    lens.className = 'arsenio-product-card__magnifier';
    lens.setAttribute('aria-hidden', 'true');
    canvas.className = 'arsenio-product-card__magnifier-canvas';
    zoomImage.classList.add('arsenio-product-card__magnifier-image');
    zoomImage.alt = '';
    zoomImage.removeAttribute('id');
    zoomImage.removeAttribute('srcset');
    zoomImage.removeAttribute('sizes');

    canvas.append(zoomImage);
    lens.append(canvas);
    media.append(lens);

    let animationFrame;
    let isMagnifying = false;
    let touchMagnifying = false;
    let mobileInView = false;
    const mobileMagnifierQuery = window.matchMedia('(max-width: 749px)');

    sourceImage.draggable = false;

    const setMagnifying = (active) => {
      isMagnifying = active;
      lens.dataset.active = String(active);
      media.dataset.magnifierActive = String(active);

      if (!active) cancelAnimationFrame(animationFrame);
    };

    const getBottleBounds = () => {
      const imageRect = sourceImage.getBoundingClientRect();
      const imageStyle = getComputedStyle(sourceImage);
      const layoutWidth = sourceImage.offsetWidth || imageRect.width;
      const layoutHeight = sourceImage.offsetHeight || imageRect.height;
      const scaleX = layoutWidth ? imageRect.width / layoutWidth : 1;
      const scaleY = layoutHeight ? imageRect.height / layoutHeight : 1;
      const paddingLeft = (Number.parseFloat(imageStyle.paddingLeft) || 0) * scaleX;
      const paddingRight = (Number.parseFloat(imageStyle.paddingRight) || 0) * scaleX;
      const paddingTop = (Number.parseFloat(imageStyle.paddingTop) || 0) * scaleY;
      const paddingBottom = (Number.parseFloat(imageStyle.paddingBottom) || 0) * scaleY;
      const contentWidth = Math.max(imageRect.width - paddingLeft - paddingRight, 1);
      const contentHeight = Math.max(imageRect.height - paddingTop - paddingBottom, 1);
      const naturalWidth = sourceImage.naturalWidth || contentWidth;
      const naturalHeight = sourceImage.naturalHeight || contentHeight;
      const objectScale = Math.min(contentWidth / naturalWidth, contentHeight / naturalHeight);
      const renderedWidth = naturalWidth * objectScale;
      const renderedHeight = naturalHeight * objectScale;

      return {
        left: imageRect.left + paddingLeft + (contentWidth - renderedWidth) / 2,
        right: imageRect.left + paddingLeft + (contentWidth + renderedWidth) / 2,
        top: imageRect.top + paddingTop + (contentHeight - renderedHeight) / 2,
        bottom: imageRect.top + paddingTop + (contentHeight + renderedHeight) / 2,
      };
    };

    const isNearBottle = (event, mediaRect) => {
      const bottleBounds = getBottleBounds();
      const tolerance = Math.max(16, Math.min(26, mediaRect.width * 0.04));

      return (
        event.clientX >= bottleBounds.left - tolerance &&
        event.clientX <= bottleBounds.right + tolerance &&
        event.clientY >= bottleBounds.top - tolerance &&
        event.clientY <= bottleBounds.bottom + tolerance
      );
    };

    const updateMagnifier = (event) => {
      const mediaRect = media.getBoundingClientRect();
      const mobileMode = mobileMagnifierQuery.matches;

      if (!mobileMode && !isNearBottle(event, mediaRect)) {
        setMagnifying(false);
        return;
      }

      const bottleBounds = getBottleBounds();
      const sourceClientX = mobileMode
        ? Math.min(Math.max(event.clientX, bottleBounds.left), bottleBounds.right)
        : event.clientX;
      const sourceClientY = mobileMode
        ? Math.min(Math.max(event.clientY, bottleBounds.top), bottleBounds.bottom)
        : event.clientY;
      const sourceX = Math.min(Math.max(sourceClientX - mediaRect.left, 0), mediaRect.width);
      const sourceY = Math.min(Math.max(sourceClientY - mediaRect.top, 0), mediaRect.height);

      if (!isMagnifying) setMagnifying(true);

      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const radius = lens.offsetWidth / 2;
        const zoom = Number.parseFloat(getComputedStyle(lens).getPropertyValue('--arsenio-magnifier-zoom')) || 2;
        const edgeInset = 4;
        const targetX = sourceX;
        const targetY = sourceY;
        const lensX = mediaRect.width <= (radius + edgeInset) * 2
          ? mediaRect.width / 2
          : Math.min(Math.max(targetX, radius + edgeInset), mediaRect.width - radius - edgeInset);
        const lensY = mediaRect.height <= (radius + edgeInset) * 2
          ? mediaRect.height / 2
          : Math.min(Math.max(targetY, radius + edgeInset), mediaRect.height - radius - edgeInset);

        lens.style.left = `${lensX}px`;
        lens.style.top = `${lensY}px`;
        canvas.style.width = `${mediaRect.width}px`;
        canvas.style.height = `${mediaRect.height}px`;
        canvas.style.transform = `translate(${radius - sourceX * zoom}px, ${radius - sourceY * zoom}px) scale(${zoom})`;
      });
    };

    const positionMobileMagnifier = () => {
      if (!mobileMagnifierQuery.matches || !mobileInView) return;

      const mediaRect = media.getBoundingClientRect();
      updateMagnifier({
        clientX: mediaRect.left + mediaRect.width * 0.5,
        clientY: mediaRect.top + mediaRect.height * 0.52,
      });
    };

    const clearTouchMagnifier = () => {
      touchMagnifying = false;
      setMagnifying(false);

      requestAnimationFrame(positionMobileMagnifier);
    };

    const setMobileVisibility = (isVisible) => {
      mobileInView = isVisible;

      if (!mobileMagnifierQuery.matches) return;
      if (!isVisible) {
        touchMagnifying = false;
        setMagnifying(false);
        lens.style.removeProperty('left');
        lens.style.removeProperty('top');
        canvas.style.removeProperty('transform');
        return;
      }

      requestAnimationFrame(() => requestAnimationFrame(positionMobileMagnifier));
    };

    const cardLink = media.closest('.arsenio-product-card');
    cardLink?.addEventListener('contextmenu', (event) => event.preventDefault());
    cardLink?.addEventListener('click', (event) => {
      if (media.dataset.suppressMagnifierClick !== 'true') return;

      event.preventDefault();
      media.dataset.suppressMagnifierClick = 'false';
    });

    media.addEventListener('pointerenter', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      updateMagnifier(event);
    });
    media.addEventListener('pointermove', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      updateMagnifier(event);
    });
    media.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch' && mobileMagnifierQuery.matches) {
        requestAnimationFrame(positionMobileMagnifier);
        return;
      }

      setMagnifying(false);
    });
    media.addEventListener('pointercancel', () => {
      setMagnifying(false);
      clearTouchMagnifier();
    });
    media.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch || !isNearBottle(touch, media.getBoundingClientRect())) return;

      event.preventDefault();
      touchMagnifying = true;
      updateMagnifier(touch);
    }, { passive: false });
    media.addEventListener('touchmove', (event) => {
      if (!touchMagnifying) return;

      const touch = event.touches[0];
      if (!touch) return;

      event.preventDefault();
      updateMagnifier(touch);
    }, { passive: false });
    media.addEventListener('touchend', (event) => {
      if (!touchMagnifying) return;

      event.preventDefault();
      media.dataset.suppressMagnifierClick = 'true';
      window.setTimeout(() => {
        media.dataset.suppressMagnifierClick = 'false';
      }, 0);
      clearTouchMagnifier();
    }, { passive: false });
    media.addEventListener('touchcancel', clearTouchMagnifier, { passive: true });
    media.addEventListener('contextmenu', (event) => event.preventDefault());
    sourceImage.addEventListener('dragstart', (event) => event.preventDefault());

    sourceImage.addEventListener('load', positionMobileMagnifier);
    requestAnimationFrame(positionMobileMagnifier);

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => setMobileVisibility(entry.isIntersecting && entry.intersectionRatio >= 0.08));
      }, { threshold: [0, 0.08, 0.25] });
      visibilityObserver.observe(sourceImage);
    } else {
      setMobileVisibility(true);
    }

    mobileMagnifierQuery.addEventListener?.('change', () => {
      if (!mobileMagnifierQuery.matches) setMagnifying(false);
      else if (mobileInView) requestAnimationFrame(positionMobileMagnifier);
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(positionMobileMagnifier);
      resizeObserver.observe(media);
      resizeObserver.observe(sourceImage);
    }
  });
}

// Product-card magnifiers are intentionally disabled for the Shopify storefront.

function initArsenioProductMagnifiers(root = document) {
  root.querySelectorAll('.arsenio-product__bottle').forEach((bottle) => {
    if (bottle.dataset.magnifierReady === 'true') return;

    const sourceImage = bottle.querySelector('.arsenio-product__bottle-image');
    if (!sourceImage) return;

    bottle.dataset.magnifierReady = 'true';

    const hitArea = document.createElement('span');
    const lens = document.createElement('span');
    const canvas = document.createElement('span');
    const zoomImage = sourceImage.cloneNode(true);

    hitArea.className = 'arsenio-product__bottle-magnifier-hit-area';
    hitArea.setAttribute('aria-hidden', 'true');
    lens.className = 'arsenio-product__bottle-magnifier';
    lens.setAttribute('aria-hidden', 'true');
    canvas.className = 'arsenio-product__bottle-magnifier-canvas';
    zoomImage.className = 'arsenio-product__bottle-magnifier-image';
    zoomImage.alt = '';
    zoomImage.removeAttribute('id');
    zoomImage.removeAttribute('srcset');
    zoomImage.removeAttribute('sizes');

    canvas.append(zoomImage);
    lens.append(canvas);
    bottle.append(hitArea, lens);

    let animationFrame;
    let touchMagnifying = false;
    let mobileInView = false;
    const mobileMagnifierQuery = window.matchMedia('(max-width: 749px)');

    sourceImage.draggable = false;

    const syncMagnifierGeometry = () => {
      const bottleRect = bottle.getBoundingClientRect();
      const imageRect = sourceImage.getBoundingClientRect();
      const tolerance = Math.max(18, Math.min(28, imageRect.width * 0.13));
      const left = imageRect.left - bottleRect.left - tolerance;
      const top = imageRect.top - bottleRect.top - tolerance;
      const width = imageRect.width + tolerance * 2;
      const height = imageRect.height + tolerance * 2;

      hitArea.style.left = `${left}px`;
      hitArea.style.top = `${top}px`;
      hitArea.style.width = `${width}px`;
      hitArea.style.height = `${height}px`;

      zoomImage.style.left = `${imageRect.left - bottleRect.left}px`;
      zoomImage.style.top = `${imageRect.top - bottleRect.top}px`;
      zoomImage.style.width = `${imageRect.width}px`;
      zoomImage.style.height = `${imageRect.height}px`;

      const currentSource = sourceImage.currentSrc || sourceImage.src;
      if (currentSource && zoomImage.src !== currentSource) zoomImage.src = currentSource;
    };

    const setActive = (active) => {
      lens.dataset.active = String(active);
      bottle.dataset.magnifierActive = String(active);
      if (!active) cancelAnimationFrame(animationFrame);
    };

    const updateMagnifier = (event) => {
      const bottleRect = bottle.getBoundingClientRect();
      const imageRect = sourceImage.getBoundingClientRect();
      const mobileMode = mobileMagnifierQuery.matches;
      const sourceClientX = mobileMode
        ? Math.min(Math.max(event.clientX, imageRect.left), imageRect.right)
        : event.clientX;
      const sourceClientY = mobileMode
        ? Math.min(Math.max(event.clientY, imageRect.top), imageRect.bottom)
        : event.clientY;
      const sourceX = sourceClientX - bottleRect.left;
      const sourceY = sourceClientY - bottleRect.top;

      setActive(true);
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const radius = lens.offsetWidth / 2;
        const zoom = Number.parseFloat(getComputedStyle(lens).getPropertyValue('--arsenio-magnifier-zoom')) || 2;
        const edgeInset = 4;
        const targetX = sourceX;
        const targetY = sourceY;
        const lensX = Math.min(Math.max(targetX, radius + edgeInset), bottleRect.width - radius - edgeInset);
        const lensY = Math.min(Math.max(targetY, radius + edgeInset), bottleRect.height - radius - edgeInset);

        lens.style.left = `${lensX}px`;
        lens.style.top = `${lensY}px`;
        canvas.style.width = `${bottleRect.width}px`;
        canvas.style.height = `${bottleRect.height}px`;
        canvas.style.transform = `translate(${radius - sourceX * zoom}px, ${radius - sourceY * zoom}px) scale(${zoom})`;
      });
    };

    const positionMobileMagnifier = () => {
      if (!mobileMagnifierQuery.matches || !mobileInView) return;

      syncMagnifierGeometry();
      const imageRect = sourceImage.getBoundingClientRect();
      updateMagnifier({
        clientX: imageRect.left + imageRect.width * 0.5,
        clientY: imageRect.top + imageRect.height * 0.58,
      });
    };

    const clearTouchMagnifier = () => {
      touchMagnifying = false;
      setActive(false);

      requestAnimationFrame(positionMobileMagnifier);
    };

    const setMobileVisibility = (isVisible) => {
      mobileInView = isVisible;

      if (!mobileMagnifierQuery.matches) return;
      if (!isVisible) {
        touchMagnifying = false;
        setActive(false);
        lens.style.removeProperty('left');
        lens.style.removeProperty('top');
        canvas.style.removeProperty('transform');
        return;
      }

      requestAnimationFrame(() => requestAnimationFrame(positionMobileMagnifier));
    };

    hitArea.addEventListener('pointerenter', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      syncMagnifierGeometry();
      updateMagnifier(event);
    });
    hitArea.addEventListener('pointermove', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      updateMagnifier(event);
    });
    hitArea.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch' && mobileMagnifierQuery.matches) {
        requestAnimationFrame(positionMobileMagnifier);
        return;
      }

      setActive(false);
    });
    hitArea.addEventListener('pointercancel', () => {
      setActive(false);
      clearTouchMagnifier();
    });
    hitArea.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;

      event.preventDefault();
      touchMagnifying = true;
      syncMagnifierGeometry();
      updateMagnifier(touch);
    }, { passive: false });
    hitArea.addEventListener('touchmove', (event) => {
      if (!touchMagnifying) return;

      const touch = event.touches[0];
      if (!touch) return;

      event.preventDefault();
      updateMagnifier(touch);
    }, { passive: false });
    hitArea.addEventListener('touchend', (event) => {
      if (!touchMagnifying) return;

      event.preventDefault();
      clearTouchMagnifier();
    }, { passive: false });
    hitArea.addEventListener('touchcancel', clearTouchMagnifier, { passive: true });
    hitArea.addEventListener('contextmenu', (event) => event.preventDefault());
    bottle.addEventListener('contextmenu', (event) => event.preventDefault());
    sourceImage.addEventListener('contextmenu', (event) => event.preventDefault());
    sourceImage.addEventListener('dragstart', (event) => event.preventDefault());

    sourceImage.addEventListener('load', () => {
      syncMagnifierGeometry();
      positionMobileMagnifier();
    });
    requestAnimationFrame(() => {
      syncMagnifierGeometry();
      positionMobileMagnifier();
    });

    if ('IntersectionObserver' in window) {
      const visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => setMobileVisibility(entry.isIntersecting && entry.intersectionRatio >= 0.08));
      }, { threshold: [0, 0.08, 0.25] });
      visibilityObserver.observe(sourceImage);
    } else {
      setMobileVisibility(true);
    }

    mobileMagnifierQuery.addEventListener?.('change', () => {
      if (!mobileMagnifierQuery.matches) setActive(false);
      else if (mobileInView) requestAnimationFrame(positionMobileMagnifier);
    });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => {
        syncMagnifierGeometry();
        positionMobileMagnifier();
      });
      resizeObserver.observe(bottle);
      resizeObserver.observe(sourceImage);
    }
  });
}

// Product-page bottle magnifiers are intentionally disabled for the Shopify storefront.

function initArsenioProductReveals(root = document) {
  root.querySelectorAll('[data-arsenio-products]').forEach((section) => {
    if (section.dataset.productRevealReady === 'true') return;

    const cards = [...section.querySelectorAll('.arsenio-product-card')];
    if (!cards.length) return;

    section.dataset.productRevealReady = 'true';

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      cards.forEach((card) => {
        card.dataset.revealVisible = 'true';
      });
      return;
    }

    cards.forEach((card) => {
      card.dataset.revealVisible = 'false';
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.revealVisible = String(entry.isIntersecting);
        });
      },
      { threshold: 0.14, rootMargin: '-3% 0px -8% 0px' },
    );

    requestAnimationFrame(() => {
      cards.forEach((card) => observer.observe(card));
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioProductReveals(), { once: true });
} else {
  initArsenioProductReveals();
}

document.addEventListener('shopify:section:load', (event) => initArsenioProductReveals(event.target));

function initArsenioStories(root = document) {
  root.querySelectorAll('[data-arsenio-story]').forEach((story) => {
    if (story.dataset.storyReady === 'true') return;

    story.dataset.storyReady = 'true';

    let revealTimer;
    let isInView = false;

    const revealStory = () => {
      window.clearTimeout(revealTimer);
      story.dataset.visible = 'false';
      revealTimer = window.setTimeout(() => {
        story.dataset.visible = 'true';
      }, 320);
    };

    if (
      story.dataset.animationEnabled !== 'true' ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      revealStory();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isInView) {
            isInView = true;
            revealStory();
          } else if (!entry.isIntersecting && isInView) {
            isInView = false;
            window.clearTimeout(revealTimer);
            story.dataset.visible = 'false';
          }
        });
      },
      { threshold: 0.18, rootMargin: '0px 0px -6% 0px' },
    );

    observer.observe(story);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioStories(), { once: true });
} else {
  initArsenioStories();
}

document.addEventListener('shopify:section:load', (event) => initArsenioStories(event.target));

function initArsenioVideos(root = document) {
  root.querySelectorAll('.arsenio-cta__media video, .arsenio-about-hero__video, .arsenio-contact-hero__video').forEach((video) => {
    const isInitialized = video.dataset.arsenioVideoInitialized === 'true';

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.dataset.arsenioVideoInitialized = 'true';

    const startVideo = () => {
      if (!video.paused) return;

      const playPromise = video.play();
      if (playPromise) playPromise.catch(() => {});
    };

    if (video.readyState >= 2 || isInitialized) {
      startVideo();
    } else {
      video.addEventListener('canplay', startVideo, { once: true });
      if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioVideos(), { once: true });
} else {
  initArsenioVideos();
}

document.addEventListener('shopify:section:load', (event) => initArsenioVideos(event.target));
window.addEventListener('pageshow', () => initArsenioVideos());
document.addEventListener('visibilitychange', () => {
  if (!document.hidden) initArsenioVideos();
});

function initArsenioContactPages(root = document) {
  root.querySelectorAll('[data-arsenio-contact-hero]').forEach((section) => {
    if (section.dataset.contactHeroReady === 'true') return;

    section.dataset.contactHeroReady = 'true';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const contactIntro = section.querySelector('.arsenio-contact-hero__content');
    const contactTitle = section.querySelector('.arsenio-contact-hero__title');
    const contactPanel = document.querySelector('[data-arsenio-contact-panel]');
    const contactRevealItems = contactPanel
      ? [
          contactPanel.querySelector('.arsenio-contact-panel__title'),
          contactPanel.querySelector('.arsenio-contact-panel__details'),
          ...contactPanel.querySelectorAll('.arsenio-contact-form__field'),
          contactPanel.querySelector('.arsenio-contact-form__status'),
          contactPanel.querySelector('.arsenio-contact-form__actions'),
        ].filter(Boolean)
      : [];
    if (contactPanel) contactPanel.dataset.contactRevealReady = 'true';
    let contactIntroObserver;
    let animationFrame;

    if (contactIntro && contactTitle && contactTitle.dataset.contactTitleSplit !== 'true') {
      contactTitle.dataset.contactTitleSplit = 'true';
      const lines = [];
      let currentLine = '';

      [...contactTitle.childNodes].forEach((node) => {
        if (node.nodeName === 'BR') {
          if (currentLine.trim()) lines.push(currentLine.trim());
          currentLine = '';
          return;
        }

        currentLine += node.textContent || '';
      });
      if (currentLine.trim()) lines.push(currentLine.trim());

      contactTitle.textContent = '';
      contactTitle.setAttribute('aria-label', lines.join(' '));
      let characterIndex = 0;

      lines.forEach((lineText) => {
        const line = document.createElement('span');
        line.className = 'arsenio-contact-hero__title-line';
        line.setAttribute('aria-hidden', 'true');

        [...lineText].forEach((character) => {
          const characterElement = document.createElement('span');
          characterElement.className = 'arsenio-contact-hero__title-char';
          characterElement.textContent = character === ' ' ? '\u00a0' : character;
          characterElement.style.setProperty('--contact-title-char-index', String(characterIndex));
          line.appendChild(characterElement);
          characterIndex += character === ' ' ? 0 : 1;
        });

        characterIndex += 2;
        contactTitle.appendChild(line);
      });

      section.dataset.contactIntroMotionReady = 'true';
      section.dataset.contactIntroMotionVisible = 'false';

      if (prefersReducedMotion.matches || !('IntersectionObserver' in window)) {
        section.dataset.contactIntroMotionVisible = 'true';
      } else {
        contactIntroObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              section.dataset.contactIntroMotionVisible = String(entry.isIntersecting);
            });
          },
          { threshold: 0.18, rootMargin: '0px 0px -5% 0px' },
        );
        contactIntroObserver.observe(contactIntro);
      }
    }

    const update = () => {
      animationFrame = undefined;

      if (prefersReducedMotion.matches) {
        section.style.removeProperty('--contact-video-scale');
        section.style.removeProperty('--contact-video-y');
        section.style.removeProperty('--contact-intro-opacity');
        contactPanel?.style.removeProperty('--contact-panel-progress');
        contactRevealItems.forEach((item) => item.style.removeProperty('--contact-item-progress'));
        document.body.classList.toggle(
          'arsenio-contact-header-dark',
          Boolean(contactPanel && contactPanel.getBoundingClientRect().top <= 96),
        );
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const scrolled = Math.max(-rect.top, 0);
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const motionStart = viewportHeight * (isMobile ? 0.6 : 0.78);
      const motionDistance = viewportHeight * (isMobile ? 0.92 : 0.9);
      const progress = Math.min(Math.max((scrolled - motionStart) / motionDistance, 0), 1);
      const minimumScale = isMobile ? 0.84 : 0.889;
      const offsetFactor = isMobile ? 0.9 : 0.97;
      const scale = minimumScale + (1 - minimumScale) * progress;
      const offset = Math.max(
        viewportHeight * offsetFactor - scrolled * (isMobile ? 1.05 : 1.12),
        0,
      );
      const introOpacity = 1;

      section.style.setProperty('--contact-video-scale', scale.toFixed(4));
      section.style.setProperty('--contact-video-y', `${offset.toFixed(2)}px`);
      section.style.setProperty('--contact-intro-opacity', introOpacity.toFixed(4));

      if (contactPanel) {
        const panelRect = contactPanel.getBoundingClientRect();
        const panelProgress = Math.min(
          Math.max((viewportHeight * 0.92 - panelRect.top) / (viewportHeight * 0.92), 0),
          1,
        );
        contactPanel.style.setProperty('--contact-panel-progress', panelProgress.toFixed(4));

        const revealStart = viewportHeight * (isMobile ? 0.94 : 0.92);
        const revealEnd = viewportHeight * (isMobile ? 0.62 : 0.64);
        const revealDistance = Math.max(revealStart - revealEnd, 1);

        contactRevealItems.forEach((item) => {
          const itemTop = item.getBoundingClientRect().top;
          const itemProgress = Math.min(Math.max((revealStart - itemTop) / revealDistance, 0), 1);
          item.style.setProperty('--contact-item-progress', itemProgress.toFixed(4));
        });

        document.body.classList.toggle('arsenio-contact-header-dark', panelRect.top <= 96);
      }
    };

    const requestUpdate = () => {
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    prefersReducedMotion.addEventListener?.('change', requestUpdate);
    section.contactPageCleanup = () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      prefersReducedMotion.removeEventListener?.('change', requestUpdate);
      contactIntroObserver?.disconnect();
      if (animationFrame) cancelAnimationFrame(animationFrame);
      document.body.classList.remove('arsenio-contact-header-dark');
    };

    requestUpdate();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioContactPages(), { once: true });
} else {
  initArsenioContactPages();
}

document.addEventListener('shopify:section:load', (event) => {
  initArsenioContactPages(event.target);
  initArsenioVideos(event.target);
});

function formatArsenioProductMoney(cents, currency) {
  const numericCents = Number(cents);
  if (!Number.isFinite(numericCents)) return '';

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
    }).format(numericCents / 100);
  } catch (_error) {
    return `${(numericCents / 100).toFixed(2)} ${currency || ''}`.trim();
  }
}

function initArsenioProductMotion(root = document) {
  root.querySelectorAll('[data-arsenio-product]').forEach((section) => {
    if (section.dataset.productMotionReady === 'true') return;

    section.dataset.productMotionReady = 'true';
    const title = section.querySelector('.arsenio-product__title');
    const revealItems = [];
    const registerReveal = (element, kind = 'text', index = 0) => {
      if (!element || element.dataset.productReveal) return;

      element.dataset.productReveal = kind;
      element.dataset.productRevealVisible = 'false';
      element.style.setProperty('--product-reveal-index', String(Math.min(index, 7)));
      revealItems.push(element);
    };

    if (title && title.dataset.productTitleSplit !== 'true') {
      title.dataset.productTitleSplit = 'true';
      title.setAttribute('aria-label', title.textContent.replace(/\s+/g, ' ').trim());
      let characterIndex = 0;

      [...title.children].forEach((line) => {
        const text = line.textContent || '';
        line.textContent = '';
        line.setAttribute('aria-hidden', 'true');

        [...text].forEach((character) => {
          const characterElement = document.createElement('span');
          characterElement.className = 'arsenio-product__title-char';
          characterElement.textContent = character === ' ' ? '\u00a0' : character;
          characterElement.style.setProperty('--product-char-index', String(characterIndex));
          line.appendChild(characterElement);
          characterIndex += character === ' ' ? 0 : 1;
        });

        characterIndex += 2;
      });
    }

    registerReveal(section.querySelector('.arsenio-product__artwork'), 'artwork');
    registerReveal(title, 'title');
    registerReveal(section.querySelector('.arsenio-product__purchase'), 'left');
    registerReveal(section.querySelector('.arsenio-product__bottle'), 'bottle');
    registerReveal(section.querySelector('.arsenio-product__stage-meta--desktop'), 'meta');
    registerReveal(section.querySelector('.arsenio-product__stage-meta--mobile'), 'meta');

    section.querySelectorAll('.arsenio-product__wine-copy, .arsenio-product__vineyard-copy').forEach((copy) => {
      const revealOffset = copy.classList.contains('arsenio-product__vineyard-copy') ? 2 : 0;
      registerReveal(copy.querySelector('h2'), 'heading');

      copy.querySelectorAll('.arsenio-product__richtext').forEach((richText) => {
        const contentItems = [
          ...richText.querySelectorAll(':scope > p, :scope > ul, :scope > ol, :scope > div > p, :scope > div > ul, :scope > div > ol'),
        ];

        if (contentItems.length) {
          contentItems.forEach((item, index) => registerReveal(item, 'text', index + revealOffset));
        } else {
          registerReveal(richText, 'text', revealOffset);
        }
      });
    });

    registerReveal(section.querySelector('.arsenio-product__vineyard-media'), 'media');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      revealItems.forEach((item) => {
        item.dataset.productRevealVisible = 'true';
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.productRevealVisible = String(entry.isIntersecting);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -6% 0px' },
    );

    revealItems.forEach((item) => observer.observe(item));
    section.productMotionObserver = observer;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioProductMotion(), { once: true });
} else {
  initArsenioProductMotion();
}

document.addEventListener('shopify:section:load', (event) => initArsenioProductMotion(event.target));

function initArsenioProductPages(root = document) {
  root.querySelectorAll('[data-arsenio-product]').forEach((section) => {
    if (section.dataset.productPageReady === 'true') return;

    const variantsNode = section.querySelector('[data-product-variants]');
    const variantInput = section.querySelector('[data-product-variant-id]');
    const bottleImage = section.querySelector('.arsenio-product__bottle-image');
    const purchaseChoice = section.querySelector('.arsenio-product__purchase-choice');
    const selectionAddButtons = [...section.querySelectorAll('[data-product-selection-add]')];
    const packBuilder = section.querySelector('[data-product-pack-builder]');
    const packSummaryCount = section.querySelector('[data-pack-total-count]');
    const packSummaryPrice = section.querySelector('[data-pack-total-price]');
    const packStatus = section.querySelector('[data-pack-status]');
    const packError = section.querySelector('[data-pack-error]');
    const checkoutLink = section.querySelector('[data-pack-checkout-link]');
    const quickSingleButtons = [...section.querySelectorAll('[data-quick-pack-action]')];
    const currency = section.dataset.currency || 'USD';
    const bottleLabel = packBuilder?.dataset.bottleLabel || 'bottle in cart';
    const bottlesLabel = packBuilder?.dataset.bottlesLabel || 'bottles in cart';
    const addLabel = purchaseChoice?.dataset.addLabel || 'Add';
    const bottleShortLabel = purchaseChoice?.dataset.bottleShortLabel || 'bottle';
    const bottlesShortLabel = purchaseChoice?.dataset.bottlesShortLabel || 'bottles';
    const productId = String(section.dataset.productId || '');
    const maxBottlesPerWine = Math.max(Number(section.dataset.maxBottlesPerWine) || 8, 1);
    const quantityLimitMessage =
      purchaseChoice?.dataset.quantityLimitMessage ||
      `A maximum of ${maxBottlesPerWine} bottles of this wine can be added to one order.`;

    if (!variantsNode || !variantInput) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsNode.textContent || '[]');
    } catch (_error) {
      variants = [];
    }

    if (!variants.length) return;

    section.dataset.productPageReady = 'true';
    let currentVariant = variants.find((variant) => String(variant.id) === String(variantInput.value)) || variants[0];
    let selectedOptions = [...(currentVariant.options || [])];
    let cartRequestInProgress = false;
    let cartWineQuantity = 0;
    const selectedPacks = new Map([
      ['single', 1],
      ['triple', 0],
    ]);

    const getSelectedBottleCount = () =>
      [...section.querySelectorAll('[data-product-pack]')].reduce((total, pack) => {
        const packSize = Math.max(Number(pack.dataset.packSize) || 1, 1);
        const packCount = Math.max(Number(selectedPacks.get(pack.dataset.productPack)) || 0, 0);
        return total + packCount * packSize;
      }, 0);

    const getWineQuantityInCart = (cart) =>
      (cart?.items || []).reduce((total, item) => {
        return String(item.product_id) === productId ? total + (Number(item.quantity) || 0) : total;
      }, 0);

    const showQuantityLimitError = () => {
      if (packStatus) packStatus.textContent = '';
      if (packError) {
        packError.textContent = quantityLimitMessage;
        packError.hidden = false;
      }
    };

    const renderPackBuilder = () => {
      if (!packBuilder) return;

      let totalBottles = 0;
      section.querySelectorAll('[data-product-pack]').forEach((pack) => {
        const packSize = Math.max(Number(pack.dataset.packSize) || 1, 1);
        const packCount = Math.max(Number(selectedPacks.get(pack.dataset.productPack)) || 0, 0);
        totalBottles += packCount * packSize;

        const count = pack.querySelector('[data-pack-count]');
        const minus = pack.querySelector('[data-pack-action="minus"]');
        const plus = pack.querySelector('[data-pack-action="plus"]');
        if (count) count.textContent = String(packCount);
        if (minus) minus.disabled = cartRequestInProgress || packCount === 0;
        if (plus) {
          plus.disabled =
            cartRequestInProgress ||
            !currentVariant.available ||
            cartWineQuantity + getSelectedBottleCount() + packSize > maxBottlesPerWine;
        }
        pack.classList.toggle('is-loading', cartRequestInProgress);
      });

      const singleBottleCount = Math.max(Number(selectedPacks.get('single')) || 0, 0);
      quickSingleButtons.forEach((button) => {
        const isMinus = button.dataset.quickPackAction === 'minus';
        button.disabled =
          cartRequestInProgress ||
          !currentVariant.available ||
          (isMinus && singleBottleCount === 0) ||
          (!isMinus && cartWineQuantity + totalBottles + 1 > maxBottlesPerWine);
      });

      const totalLabel = `${totalBottles} ${totalBottles === 1 ? bottleLabel : bottlesLabel}`;
      if (packSummaryCount) packSummaryCount.textContent = totalLabel;
      if (packSummaryPrice) {
        packSummaryPrice.textContent = formatArsenioProductMoney(currentVariant.price * totalBottles, currency);
      }

      const shortLabel = totalBottles === 1 ? bottleShortLabel : bottlesShortLabel;
      const buttonLabel = `${addLabel} ${totalBottles} ${shortLabel} — ${formatArsenioProductMoney(
        currentVariant.price * totalBottles,
        currency,
      )}`;
      selectionAddButtons.forEach((button) => {
        const label = button.querySelector('[data-product-selection-add-label]');
        const spinner = button.querySelector('.loading__spinner');
        if (label) label.textContent = buttonLabel;
        if (spinner) spinner.hidden = !cartRequestInProgress;
        button.disabled =
          cartRequestInProgress ||
          !currentVariant.available ||
          totalBottles === 0 ||
          cartWineQuantity + totalBottles > maxBottlesPerWine;
        button.classList.toggle('is-loading', cartRequestInProgress);
      });
    };

    const readCart = async () => {
      const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to load the cart.');
      return response.json();
    };

    const updatePackSelectionByKey = (packKey, direction) => {
      if (!packKey || cartRequestInProgress || !currentVariant.available) return;
      const pack = section.querySelector(`[data-product-pack="${packKey}"]`);
      const packSize = Math.max(Number(pack?.dataset.packSize) || 1, 1);
      if (direction > 0 && cartWineQuantity + getSelectedBottleCount() + packSize > maxBottlesPerWine) {
        showQuantityLimitError();
        return;
      }
      const currentCount = Math.max(Number(selectedPacks.get(packKey)) || 0, 0);
      selectedPacks.set(packKey, Math.max(currentCount + direction, 0));
      if (packStatus) packStatus.textContent = '';
      if (packError) {
        packError.hidden = true;
        packError.textContent = '';
      }
      renderPackBuilder();
    };

    const updatePackSelection = (pack, direction) => {
      updatePackSelectionByKey(pack?.dataset.productPack, direction);
    };

    const addSelectionToCart = async () => {
      if (cartRequestInProgress || !currentVariant.available) return;

      const items = [...section.querySelectorAll('[data-product-pack]')]
        .map((pack) => {
          const packKey = pack.dataset.productPack;
          const packSize = Math.max(Number(pack.dataset.packSize) || 1, 1);
          const packCount = Math.max(Number(selectedPacks.get(packKey)) || 0, 0);
          return {
            id: Number(currentVariant.id),
            quantity: packCount * packSize,
            properties: { _ArsenioPack: packKey },
          };
        })
        .filter((item) => item.quantity > 0);

      if (!items.length) return;

      const addedBottleCount = items.reduce((total, item) => total + item.quantity, 0);

      cartRequestInProgress = true;
      if (packStatus) packStatus.textContent = '';
      if (packError) {
        packError.hidden = true;
        packError.textContent = '';
      }
      renderPackBuilder();

      try {
        const currentCart = await readCart();
        cartWineQuantity = getWineQuantityInCart(currentCart);
        if (cartWineQuantity + addedBottleCount > maxBottlesPerWine) {
          throw new Error(quantityLimitMessage);
        }

        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items }),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Unable to update the cart.');
        }

        const cart = await readCart();
        cartWineQuantity = getWineQuantityInCart(cart);
        if (packStatus) {
          const addedLabel = addedBottleCount === 1 ? bottleShortLabel : bottlesShortLabel;
          const cartLabel = cart.item_count === 1 ? bottleLabel : bottlesLabel;
          packStatus.textContent = `Added ${addedBottleCount} ${addedLabel} · ${cart.item_count} ${cartLabel}`;
        }
        if (checkoutLink) checkoutLink.hidden = false;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'arsenio-pack-builder',
            productVariantId: String(currentVariant.id),
            cartData: cart,
          });
        }
      } catch (error) {
        if (packError) {
          packError.textContent = error.message || 'Unable to update the cart.';
          packError.hidden = false;
        }
      } finally {
        cartRequestInProgress = false;
        renderPackBuilder();
      }
    };

    const updateVariant = (variant) => {
      if (!variant) return;

      currentVariant = variant;
      selectedOptions = [...(variant.options || selectedOptions)];
      variantInput.value = variant.id;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));

      const variantImage = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
      if (variantImage && bottleImage) {
        bottleImage.style.opacity = '0';
        window.setTimeout(() => {
          bottleImage.removeAttribute('srcset');
          bottleImage.removeAttribute('sizes');
          bottleImage.src = variantImage;
          bottleImage.style.opacity = '1';
        }, 140);
      }

      if (packStatus) packStatus.textContent = '';
      selectedPacks.set('single', 1);
      selectedPacks.set('triple', 0);
      renderPackBuilder();

      if (!window.Shopify?.designMode && window.history?.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    section.querySelectorAll('[data-product-option]').forEach((option) => {
      option.addEventListener('change', () => {
        const position = Math.max(Number(option.dataset.optionPosition) - 1, 0);
        selectedOptions[position] = option.value;
        const matchingVariant = variants.find((variant) =>
          variant.options.every((value, index) => value === selectedOptions[index]),
        );
        updateVariant(matchingVariant);
      });
    });

    section.querySelectorAll('[data-product-pack]').forEach((pack) => {
      pack.querySelectorAll('[data-pack-action]').forEach((button) => {
        button.addEventListener('click', () => {
          const direction = button.dataset.packAction === 'minus' ? -1 : 1;
          updatePackSelection(pack, direction);
        });
      });
    });

    quickSingleButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const direction = button.dataset.quickPackAction === 'minus' ? -1 : 1;
        updatePackSelectionByKey('single', direction);
      });
    });

    selectionAddButtons.forEach((button) => {
      button.addEventListener('click', addSelectionToCart);
    });

    renderPackBuilder();
    readCart()
      .then((cart) => {
        cartWineQuantity = getWineQuantityInCart(cart);
        renderPackBuilder();
      })
      .catch(() => {
        // The add-to-cart request rechecks the cart before changing it.
      });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioProductPages(), { once: true });
} else {
  initArsenioProductPages();
}

document.addEventListener('shopify:section:load', (event) => initArsenioProductPages(event.target));

function initArsenioAboutHeroes(root = document) {
  root.querySelectorAll('[data-arsenio-about-hero]').forEach((section) => {
    if (section.dataset.aboutHeroReady === 'true') return;

    section.dataset.aboutHeroReady = 'true';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let animationFrame;

    const update = () => {
      animationFrame = undefined;

      if (prefersReducedMotion.matches) {
        section.style.removeProperty('--about-video-scale');
        section.style.removeProperty('--about-video-y');
        section.style.removeProperty('--about-intro-opacity');
        document.body.classList.remove('arsenio-about-header-dark');
        return;
      }

      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const availableScroll = Math.max(section.offsetHeight - viewportHeight, 1);
      const progress = Math.min(Math.max(-rect.top / availableScroll, 0), 1);
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const minimumScale = isMobile ? 0.82 : 0.6;
      const offsetFactor = isMobile ? 0.78 : 0.72;
      const scale = minimumScale + (1 - minimumScale) * progress;
      const offset = viewportHeight * offsetFactor * Math.pow(1 - progress, 1.5);
      const introOpacity = Math.max(1 - progress * 3.15, 0);

      section.style.setProperty('--about-video-scale', scale.toFixed(4));
      section.style.setProperty('--about-video-y', `${offset.toFixed(2)}px`);
      section.style.setProperty('--about-intro-opacity', introOpacity.toFixed(4));
      document.body.classList.toggle('arsenio-about-header-dark', progress > 0.28);
    };

    const requestUpdate = () => {
      if (animationFrame) return;
      animationFrame = requestAnimationFrame(update);
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    prefersReducedMotion.addEventListener?.('change', requestUpdate);
    section.aboutHeroCleanup = () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      prefersReducedMotion.removeEventListener?.('change', requestUpdate);
      if (animationFrame) cancelAnimationFrame(animationFrame);
      document.body.classList.remove('arsenio-about-header-dark');
    };

    requestUpdate();
  });
}

function initArsenioAboutReveals(root = document) {
  root.querySelectorAll('[data-arsenio-about-reveals]').forEach((container) => {
    if (container.dataset.aboutRevealReady === 'true') return;

    const items = [...container.querySelectorAll('[data-about-reveal]')];
    if (!items.length) return;

    container.dataset.aboutRevealReady = 'true';
    items.forEach((item, index) => {
      item.style.setProperty('--about-reveal-index', Math.min(index, 5));
      item.dataset.revealVisible = 'false';
    });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => {
        item.dataset.revealVisible = 'true';
      });
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.dataset.revealVisible = String(entry.isIntersecting);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' },
    );

    items.forEach((item) => observer.observe(item));
    container.aboutRevealObserver = observer;
  });
}

function initArsenioAboutGalleries(root = document) {
  root.querySelectorAll('[data-arsenio-about-gallery]').forEach((gallery) => {
    if (gallery.dataset.aboutGalleryReady === 'true') return;

    const track = gallery.querySelector('[data-about-gallery-track]');
    const previous = gallery.querySelector('[data-about-gallery-previous]');
    const next = gallery.querySelector('[data-about-gallery-next]');
    if (!track) return;

    gallery.dataset.aboutGalleryReady = 'true';
    const items = [...track.querySelectorAll('.arsenio-about-gallery__item')];
    let touchState = null;

    const inlineStart = () => parseFloat(window.getComputedStyle(track).paddingInlineStart) || 0;
    const itemDistanceFromStart = (item) => {
      const trackRect = track.getBoundingClientRect();
      return item.getBoundingClientRect().left - trackRect.left - inlineStart();
    };

    const nearestItem = () => {
      if (!items.length) return null;

      return items.reduce((nearest, item) => {
        const distance = Math.abs(itemDistanceFromStart(item));
        if (!nearest || distance < nearest.distance) return { item, distance };
        return nearest;
      }, null)?.item;
    };

    const scrollToItem = (item, behavior = 'smooth') => {
      if (!item) return;
      track.scrollTo({ left: track.scrollLeft + itemDistanceFromStart(item), behavior });
    };

    const move = (direction) => {
      if (!items.length) return;
      const current = nearestItem();
      const currentIndex = Math.max(0, items.indexOf(current));
      const nextIndex = Math.min(items.length - 1, Math.max(0, currentIndex + direction));
      scrollToItem(items[nextIndex]);
    };

    const handleTouchStart = (event) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchState = {
        axis: null,
        startX: touch.clientX,
        startY: touch.clientY,
        startScrollLeft: track.scrollLeft,
      };
    };

    const handleTouchMove = (event) => {
      if (!touchState || event.touches.length !== 1) return;
      const touch = event.touches[0];
      const deltaX = touch.clientX - touchState.startX;
      const deltaY = touch.clientY - touchState.startY;

      if (!touchState.axis) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 8) return;
        touchState.axis = Math.abs(deltaX) > Math.abs(deltaY) * 1.15 ? 'x' : 'y';
        if (touchState.axis === 'x') track.dataset.galleryDragging = 'true';
      }

      if (touchState.axis !== 'x') return;
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = touchState.startScrollLeft - deltaX;
    };

    const handleTouchEnd = () => {
      if (!touchState) return;
      const wasHorizontal = touchState.axis === 'x';
      touchState = null;
      delete track.dataset.galleryDragging;

      if (wasHorizontal) {
        requestAnimationFrame(() => scrollToItem(nearestItem()));
      }
    };

    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    track.addEventListener('touchstart', handleTouchStart, { passive: true });
    track.addEventListener('touchmove', handleTouchMove, { passive: false });
    track.addEventListener('touchend', handleTouchEnd, { passive: true });
    track.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    gallery.aboutGalleryCleanup = () => {
      track.removeEventListener('touchstart', handleTouchStart);
      track.removeEventListener('touchmove', handleTouchMove);
      track.removeEventListener('touchend', handleTouchEnd);
      track.removeEventListener('touchcancel', handleTouchEnd);
    };
  });
}

function initArsenioAbout(root = document) {
  initArsenioAboutHeroes(root);
  initArsenioAboutReveals(root);
  initArsenioAboutGalleries(root);
  initArsenioVideos(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initArsenioAbout(), { once: true });
} else {
  initArsenioAbout();
}

document.addEventListener('shopify:section:load', (event) => initArsenioAbout(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-arsenio-contact-hero]').forEach((section) => section.contactPageCleanup?.());
  event.target.querySelectorAll('[data-arsenio-about-hero]').forEach((section) => section.aboutHeroCleanup?.());
  event.target.querySelectorAll('[data-arsenio-product]').forEach((section) => section.productMotionObserver?.disconnect());
  event.target.querySelectorAll('[data-arsenio-about-reveals]').forEach((container) => container.aboutRevealObserver?.disconnect());
  event.target.querySelectorAll('[data-arsenio-about-gallery]').forEach((gallery) => gallery.aboutGalleryCleanup?.());
});

function initArsenioAgeGate() {
  const gate = document.querySelector('[data-arsenio-age-gate]');
  if (!gate) return;

  const storageKey = 'arsenio-age-verified-v1';
  const isVerified = () => {
    try {
      return window.sessionStorage.getItem(storageKey) === 'true';
    } catch (_) {
      return false;
    }
  };

  const showGate = () => {
    gate.hidden = false;
    gate.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('arsenio-age-gate-open');
    document.body.classList.add('arsenio-age-gate-open');
    gate.querySelector('[data-arsenio-age-yes]')?.focus();
  };

  if (isVerified()) return;
  showGate();

  gate.querySelector('[data-arsenio-age-yes]')?.addEventListener('click', () => {
    try {
      window.sessionStorage.setItem(storageKey, 'true');
    } catch (_) {}
    gate.hidden = true;
    gate.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('arsenio-age-gate-open');
    document.body.classList.remove('arsenio-age-gate-open');
  });

  gate.querySelector('[data-arsenio-age-no]')?.addEventListener('click', () => {
    const prompt = gate.querySelector('[data-arsenio-age-prompt]');
    const denied = gate.querySelector('[data-arsenio-age-denied]');
    if (!prompt || !denied) return;
    prompt.hidden = true;
    denied.hidden = false;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initArsenioAgeGate, { once: true });
} else {
  initArsenioAgeGate();
}